/**
 * 모듈 융합 커맨드 (S2c 후반) — GameScene performModuleFusion/canFuseNow/commitFusion 추출 (Phaser-free).
 *
 * 같은 footprint 2개 + 비용(첫 회 광고 / adFree 첫 회 무료 / 이후 다이아 고정) → 두 효과 합친 모듈.
 * 광고/토스트는 셸 콜백 deps(DailyShopCommands 패턴 — ports 확장 없이 자체 deps), 컨트롤러는 ctx getter.
 * 재진입 가드(fusion.tryBegin/end)로 광고 콜백 대기 중 이중 융합 레이스를 차단한다.
 */
import { BigNumber } from "@yjsgames/bignumber";
import { canFuse, fuseModules } from "../../systems/ModuleFusion.js";
import { t } from "../../i18n/i18n.js";
import { diag } from "../diag.js";
import type { GameContext } from "../GameContext.js";
import type { ShellPorts } from "./ports.js";

const log = diag("compose");

export interface FusionCommandDeps {
  ctx: GameContext;
  ports: ShellPorts;
  adFree: () => boolean;
  /** 보상형 광고 — onReward에서만 융합 진행, 스킵/실패는 가드 해제. */
  showRewardedAd: (callbacks: { onReward: () => void; onSkip: () => void }) => void;
  showToast: (message: string) => void;
}

export class FusionCommands {
  constructor(private readonly deps: FusionCommandDeps) {}

  /**
   * 비용 분기: ad → 광고 시청 후 commit / diamond → 차감(부족 시 토스트) / free(adFree 첫 회) → 즉시.
   * 광고 콜백 사이 stale 방지를 위해 commit이 canFuseNow를 재검증한다. AutoPlay FUSE_MODULES 시그니처.
   */
  fuse(materialIds: readonly string[]): void {
    const { ctx, ports } = this.deps;
    if (!this.canFuseNow(materialIds)) {
      ports.invalidateUi();
      return;
    }
    // 재진입 가드 — 광고 대기 중 두 번째 융합이 둘 다 count 0(무료)으로 처리되는 레이스 차단.
    // 모든 종료 경로(commit 성공/재검증 실패, 광고 스킵, 결제 실패)에서 fusion.end()로 해제.
    if (!ctx.fusion().tryBegin()) {
      ports.invalidateUi();
      return;
    }
    const cost = ctx.fusion().cost(this.deps.adFree());
    log.diag("fuse", `cost=${cost.kind} mats=${materialIds.join(",")}`);
    if (cost.kind === "ad") {
      this.deps.showRewardedAd({
        onReward: () => this.commit(materialIds),
        onSkip: () => ctx.fusion().end(), // 광고 스킵/실패 → 융합 안 함 + 가드 해제
      });
      return;
    }
    if (cost.kind === "diamond") {
      if (!ctx.economy().trySpendDiamond(BigNumber.fromNumber(cost.amount))) {
        ctx.fusion().end(); // 결제 실패 → 가드 해제
        this.deps.showToast(t("toast.diamondShort", { cost: cost.amount }));
        ports.invalidateUi();
        return;
      }
    }
    // free(adFree 첫 회) 또는 다이아 결제 완료 → 즉시 융합.
    this.commit(materialIds);
  }

  /** 융합 재검증 — 재료 2개 전부 보유 + 미배치 + canFuse(같은 footprint·미융합). 광고 콜백 stale 방지 공용. */
  private canFuseNow(materialIds: readonly string[]): boolean {
    const inv = this.deps.ctx.inventory();
    const mats = materialIds
      .map((id) => inv.getInstance(id))
      .filter((i): i is NonNullable<typeof i> => !!i);
    if (mats.length !== materialIds.length) return false;
    const placed = new Set(this.deps.ctx.grid().placedInstanceIds());
    if (mats.some((m) => placed.has(m.instanceId))) return false;
    return canFuse(mats);
  }

  /** 비용 결제 후 실제 융합 — 재검증 → fuseModules → 인벤토리 갱신 → markUsed → 즉시 저장 → 토스트. */
  private commit(materialIds: readonly string[]): void {
    const { ctx, ports } = this.deps;
    // 가드 해제(end)는 성공/재검증 실패 모든 경로에서 — tryBegin과 짝.
    if (!this.canFuseNow(materialIds)) {
      ctx.fusion().end();
      ports.invalidateUi();
      return;
    }
    const inv = ctx.inventory();
    const mats = materialIds.map((id) => inv.getInstance(id)!);
    const defOf = (id: string) => ctx.moduleRegistry().getDefinition(id);
    const { fused, consumedIds } = fuseModules(mats, defOf, ctx.idSource()());
    for (const id of consumedIds) inv.removeInstance(id);
    inv.addInstance(fused);
    ctx.fusion().markUsed();
    ctx.fusion().end();
    ctx.soundController().play("levelup");
    log.diag("commit", `fused=${fused.instanceId}(${fused.definitionId})`);
    ports.persistNow(); // 다이아 소비 + 융합 결과 — 즉시 저장(유료 재화 유실 방지)
    ports.invalidateUi(); // battleTab+활성 탭 갱신 (숨은 탭은 표시 시점 — G3 의미 보존)
    this.deps.showToast(t("toast.fusionDone", { name: t(`module.${fused.definitionId}.name`) }));
  }
}
